import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Calendar, User, ArrowLeft, Clock, Tag } from 'lucide-react';
import axios from 'axios';
import { useStructuredData, useMultipleStructuredData } from '../hooks/useStructuredData';
import { generateBlogPostingSchema } from '../utils/structuredData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPage = ({ onBackToHome }) => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);

  // Generate structured data for blog posts
  const blogSchemas = React.useMemo(() => {
    if (selectedPost) {
      // Single blog post view
      return [generateBlogPostingSchema(selectedPost)];
    } else if (blogPosts.length > 0) {
      // Blog listing view
      return blogPosts.map(post => generateBlogPostingSchema(post));
    }
    return [];
  }, [blogPosts, selectedPost]);

  // Apply structured data
  useMultipleStructuredData(blogSchemas, 'blog-schema');

  useEffect(() => {
    const fetchAllBlogs = async () => {
      try {
        const response = await axios.get(`${API}/blog/recent?limit=20`);
        setBlogPosts(response.data);
        console.log('All blog posts fetched:', response.data);
      } catch (error) {
        console.error('Error fetching all blog posts:', error);
        // Fallback demo data with actual case study content
        setBlogPosts([
          {
            id: '1',
            title: 'Unlock the Power of Live Discounts - A Case Study',
            excerpt: 'Explore how AI conversational chatbots and live QR discounts are revolutionizing retail engagement, driving customers into physical stores and significantly increasing sales through real-time personalized offers.',
            content: `**Introduction:**

The retail industry has always been a fiercely competitive market, with retailers constantly searching for creative and innovative ways to bring customers into their stores. One such approach is the introduction of live discounts for bricks and mortar businesses, a popular technique that is gaining popularity amongst retailers.

This case study report aims to explore the benefits of using live discounts and conversational chatbots in retail businesses, specifically analysing the impact of the Shoppers' Discount Club App on sales and footfall.

## Background:

Live discounts are the current trend that is gaining momentum amongst bricks and mortar retailers. The concept involves offering discounts on products in real time to attract passing shoppers into their retail store. These live discounts are executed in one way using AI conversational chatbot bespoke to the retailers offering which is instantly scannable by the shopper via a QR code displayed in their shop window, door or counter.

## Case Study:

In this case study, we consider the success story of a retail business that utilised the benefits of the Shoppers' Discount Club AI conversational chatbots to offer live discounts to its customers.

### Step 1: Implementation of Shoppers' Discount Club App

Shoppers' Discount Club is an AI conversational chatbot app that offers customers exclusive discounts on products instantly in real time. The retail business initiated this app with a variety of discounts to various groups of people to attract more customers and increase footfall.

## Results:

The implementation of the Shoppers' Discount Club conversational chatbots resulted in a significant increase in sales and footfall for the retail business.

### 1. Increase in footfall: 
With the successful integration of the AI conversational chatbots, the retail store witnessed a significant increase in footfall as passing shoppers scanned to see what offer they could get in real time.

### 2. Increase in sales: 
Live discounts and personalised product recommendations resulted in an increase in sales.

### 3. Customer satisfaction: 
The personalised approach added to the shopping experience as the conversational chatbots increased engagement.

## Conclusion:

This case study highlights the benefits of offering live discounts through the use of conversational chatbots in retail businesses. The integration of the Shoppers' Discount Club AI chatbots proved to be a successful strategy to attract more customers, increase footfall and sales.

---

**Ready to transform your business?** 

You can sign up for our service by visiting '[Request to be Listed](/business-application)' - fill out our application form so your business can start enjoying the benefits of our platform.`,
            author: 'Shoppers Discount Club Team',
            category: 'Case Studies',
            tags: ['AI Chatbots', 'Live Discounts', 'QR Codes'],
            is_featured: true,
            created_at: '2025-09-27T05:58:38.650261Z'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchAllBlogs();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      'case-studies': 'bg-blue-100 text-blue-800',
      'technology': 'bg-green-100 text-green-800',
      'retail-insights': 'bg-purple-100 text-purple-800',
      'marketing': 'bg-orange-100 text-orange-800',
      'default': 'bg-gray-100 text-gray-800'
    };
    return colors[category?.toLowerCase()] || colors.default;
  };

  if (selectedPost) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <Button 
            onClick={() => setSelectedPost(null)}
            variant="outline"
            className="mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to All Articles
          </Button>

          <article className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
            {selectedPost.featured_image_url && (
              <div className="aspect-video bg-gradient-to-br from-primary/10 to-orange/10">
                <img 
                  src={selectedPost.featured_image_url} 
                  alt={selectedPost.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            
            <div className="p-8">
              <div className="flex items-center gap-4 mb-4">
                <Badge className={getCategoryColor(selectedPost.category)}>
                  {selectedPost.category || 'Article'}
                </Badge>
                {selectedPost.is_featured && (
                  <Badge className="bg-amber-100 text-amber-800">
                    ⭐ Featured
                  </Badge>
                )}
              </div>

              <h1 className="text-4xl font-bold text-charcoal mb-4">
                {selectedPost.title}
              </h1>

              <div className="flex items-center gap-6 text-gray-600 mb-6">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span>{selectedPost.author}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>{formatDate(selectedPost.created_at)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>5 min read</span>
                </div>
              </div>

              {selectedPost.tags && selectedPost.tags.length > 0 && (
                <div className="flex items-center gap-2 mb-6">
                  <Tag className="h-4 w-4 text-gray-500" />
                  <div className="flex flex-wrap gap-2">
                    {selectedPost.tags.map((tag, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="prose max-w-none">
                <div className="text-lg text-gray-700 mb-6 p-4 bg-gray-50 rounded-lg border-l-4 border-primary">
                  {selectedPost.excerpt}
                </div>
                
                <div className="whitespace-pre-wrap text-gray-800 leading-relaxed">
                  {selectedPost.content}
                </div>
              </div>
            </div>
          </article>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading articles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-charcoal mb-2">
              Shopping Insights & Articles
            </h1>
            <p className="text-gray-600 text-lg">
              Expert insights, case studies, and tips to help you save more and shop smarter
            </p>
          </div>
          <Button 
            onClick={onBackToHome}
            variant="outline"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </div>

        {blogPosts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600">No articles available at the moment.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {blogPosts.map((post) => (
              <Card 
                key={post.id} 
                className="group hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden"
                onClick={() => setSelectedPost(post)}
              >
                <div className="aspect-video bg-gradient-to-br from-primary/10 to-orange/10">
                  {post.featured_image_url ? (
                    <img 
                      src={post.featured_image_url} 
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-primary/30 text-6xl">📝</div>
                    </div>
                  )}
                </div>
                
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-2">
                    <Badge className={getCategoryColor(post.category)}>
                      {post.category || 'Article'}
                    </Badge>
                    {post.is_featured && (
                      <Badge className="bg-amber-100 text-amber-800">
                        ⭐ Featured
                      </Badge>
                    )}
                  </div>

                  <h3 className="font-bold text-lg group-hover:text-primary transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  
                  <p className="text-gray-600 text-sm line-clamp-3">
                    {post.excerpt}
                  </p>

                  <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t">
                    <div className="flex items-center gap-2">
                      <User className="h-3 w-3" />
                      <span>{post.author}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-3 w-3" />
                      <span>{formatDate(post.created_at)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPage;